/**
 * describeArtifactTriggerPast — формулирует «за что» получен артефакт
 * в past tense для reveal-overlay'я. Отличается от atlasNextUnlock
 * (там «ещё N» в future tense про незакрытые).
 *
 * JB 2026-05-26: «слишком непонятно за что мы дали ту или иную память
 * пути и что её получение обозначает для геймплея».
 */
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import type { ArtifactTrigger } from './types.js';

export function describeArtifactTriggerPast(trigger: ArtifactTrigger): string {
  switch (trigger.kind) {
    case 'crystallizationsAtLeast':
    case 'crystallizationsExactly': {
      const n = trigger.value;
      if (n === 1) return 'за первую кристаллизацию';
      if (n === 10) return 'за десять кристаллизаций';
      if (n === 50) return 'за пятьдесят кристаллизаций';
      return `за ${n.toString()} кристаллизаций`;
    }
    case 'totalTapsAtLeast': {
      const n = trigger.value;
      if (n === 100) return 'за первую сотню тапов';
      if (n === 1000) return 'за первую тысячу тапов';
      return `за ${n.toString()} тапов`;
    }
    case 'spiritBallsAtLeast':
      return `за ${trigger.value.toString()} духобаллов`;
    case 'rhythmStreakReached':
      return `за серию из ${trigger.value.toString()} тапов в ритм`;
    case 'epochReached':
      return `за переход в эпоху ${trigger.epoch.toString()}`;
    case 'profileNameSet':
      return 'за то что назвал себя';
    case 'upgradePurchasedExactly': {
      // Найдём human-имя апгрейда в каталоге.
      const upgrade = UPGRADES_ALPHA.find((u) => u.id === trigger.upgradeId);
      const name = upgrade?.name ?? trigger.upgradeId;
      if (trigger.level === 1) return `за покупку «${name}»`;
      return `за «${name}» ур. ${trigger.level.toString()}`;
    }
    case 'lessonsUnlockedAtLeast':
      return `за ${trigger.value.toString()} открытых уроков`;
    case 'phrasesCollectedAtLeast':
      return `за ${trigger.value.toString()} собранных фраз`;
    case 'daysPlayedAtLeast': {
      const n = trigger.value;
      if (n === 1) return 'за первый день';
      if (n === 7) return 'за неделю в игре';
      if (n === 30) return 'за месяц в игре';
      return `за ${n.toString()} дней игры`;
    }
    case 'activationsSeenAtLeast':
      return `за ${trigger.value.toString()} активаций Сай`;
    case 'goldenSparksCaughtAtLeast': {
      const n = trigger.value;
      if (n === 1) return 'за первую Золотую Искру';
      return `за ${n.toString()} Золотых Искр`;
    }
    case 'totalUpgradesAtLeast':
      return `за ${trigger.value.toString()} купленных апгрейдов`;
    case 'firstTapInTimeWindow':
      return `за первый тап в часы ${trigger.fromHour.toString()}–${trigger.toHour.toString()}`;
  }
}
